import { Button, Checkbox, CloseButton, Group, Stack, TextInput } from "@mantine/core"
import { modals } from "@mantine/modals"
import { useRouter } from "next/navigation"
import { useState } from "react"
import { RoleSelect } from "@/components/forms"
import { useAuth, useUserOption } from "@/hooks"
import type { TUser } from "@/types"
import { profileURL } from "@/utils"
import { useImpersonate } from "@/utils/impersonate"

type TRecentImpersonatedUser = {
  id: string
  name?: string
}

export function Impersonate() {
  const auth = useAuth()
  const { user } = auth
  const { push } = useRouter()
  const [role, setRole] = useState<string | null>(user?.role ?? null)
  const { reset, setImpersonate, value } = useImpersonate()
  const [superuser, setSuper] = useState(!!user?.is_super)
  const [userId, setUserId] = useState(value?.id || "")
  const impersonateId = value?.id
  const [iids, setIids] = useUserOption("iids", [] as Array<string | TRecentImpersonatedUser>)
  const recentUsers = iids
    .map((item) => {
      if (!item) return null
      if (typeof item === "string") return { id: item, name: item }
      if (!item.id) return null
      return item
    })
    .filter((item): item is TRecentImpersonatedUser => !!item)
  const selectedUserId = impersonateId || userId

  const upsertRecentUser = (id: string, name?: string) => {
    setIids([{ id, name: name || id }, ...recentUsers.filter((u) => u.id !== id)].slice(0, 10))
  }

  if (!user?.is_super && !value) return null

  return (
    <Stack>
      <TextInput
        disabled={!!impersonateId}
        value={impersonateId || userId}
        label="Select User ID"
        onChange={(e) => {
          const value = e.currentTarget.value.trim()
          setUserId(value)
        }}
      />
      {recentUsers.length > 0 && (
        <Group>
          {recentUsers.map((recentUser) => (
            <Group key={recentUser.id} gap={2} wrap="nowrap">
              <Button
                variant={recentUser.id === selectedUserId ? "filled" : "outline"}
                color={recentUser.id === selectedUserId ? "dark" : undefined}
                size="compact-xs"
                onClick={() => setUserId(recentUser.id)}
              >
                {recentUser.name || recentUser.id}
              </Button>
              <CloseButton
                size="sm"
                onClick={() => {
                  setIids(recentUsers.filter((u) => u.id !== recentUser.id))
                }}
              />
            </Group>
          ))}
        </Group>
      )}
      <RoleSelect value={role} onChange={setRole} clearable disabled={!!impersonateId || userId} />
      <Checkbox
        label="Super User"
        checked={superuser}
        onChange={() => setSuper((v) => !v)}
        disabled={!!impersonateId || userId}
      />
      <Group>
        <Button
          color="red"
          onClick={() => {
            modals.closeAll()
            reset()
            push(profileURL)
          }}
          disabled={!value}
        >
          Reset
        </Button>
        <Button variant="outline" onClick={() => modals.closeAll()} ml="auto">
          Cancel
        </Button>
        <Button
          disabled={!!impersonateId}
          onClick={async () => {
            if (userId) {
              upsertRecentUser(userId)
              const name = await setImpersonate({ id: userId }, auth)
              upsertRecentUser(userId, name)
              modals.closeAll()
              push(profileURL)
              return
            }

            await setImpersonate(
              {
                user: {
                  is_super: superuser,
                  role: role?.length ? (role as TUser["role"]) : user?.role,
                },
              },
              auth,
            )
            modals.closeAll()
          }}
        >
          Save
        </Button>
      </Group>
    </Stack>
  )
}
